/* Sign-in / create-account / password-reset modal. Moved verbatim from
 * player/src/AuthModal.tsx; the auth client, the "signed in" callback, the
 * OAuth link builder and the site origin are injected so the launcher can use
 * the same modal (its OAuth buttons appear only when it can complete them).
 *
 * Reuses the HeroUI Modal + the site's field-input styling (same as Settings).
 * OAuth/Steam buttons are full-navigation redirects to the backend on the
 * site; the email + password form posts to /api/auth/* and, on success,
 * calls onSignedIn and closes. Render it conditionally — mounted == open. */
import { useEffect, useState, type FormEvent } from 'react'
import { Button, Modal } from '@heroui/react'

export type AuthProvider = 'discord' | 'google' | 'steam'
export type AuthMode = 'signin' | 'signup' | 'forgot' | 'reset'

/* What the modal needs from an API client (the site's `auth`, the launcher's IPC one). */
export interface AuthClient {
  providers(): Promise<Record<AuthProvider, boolean>>
  register(email: string, password: string, ref?: string | null, updates?: boolean): Promise<{ ok: boolean; needs_verify?: boolean }>
  login(email: string, password: string): Promise<{ ok: boolean }>
  requestReset(email: string): Promise<{ ok: boolean }>
  reset(token: string, password: string): Promise<{ ok: boolean }>
}

const PROVIDER_LABEL: Record<AuthProvider, string> = { steam: 'Steam', discord: 'Discord', google: 'Google' }

// Small inline brand glyphs so we don't depend on the icon set having all three.
function ProviderGlyph({ p }: { p: AuthProvider }) {
  if (p === 'discord') return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M20.3 4.4A19.8 19.8 0 0 0 15.4 3l-.25.5a18.3 18.3 0 0 1 4.3 1.4 17.6 17.6 0 0 0-15 0A18.3 18.3 0 0 1 8.85 3.5L8.6 3a19.8 19.8 0 0 0-4.9 1.4C.9 8.6.13 12.7.5 16.7a19.9 19.9 0 0 0 6.05 3.06l.74-1.18a12.9 12.9 0 0 1-2.04-.98l.5-.37a14.2 14.2 0 0 0 12.5 0l.5.37c-.65.39-1.34.72-2.05.98l.74 1.18A19.9 19.9 0 0 0 23.5 16.7c.43-4.64-.74-8.7-3.2-12.3ZM8.5 14.3c-.98 0-1.78-.9-1.78-2s.78-2 1.78-2 1.8.9 1.78 2c0 1.1-.79 2-1.78 2Zm7 0c-.98 0-1.78-.9-1.78-2s.78-2 1.78-2 1.8.9 1.78 2c0 1.1-.78 2-1.78 2Z" /></svg>
  )
  if (p === 'google') return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden><path fill="#4285F4" d="M22 12.2c0-.7-.06-1.4-.18-2H12v3.8h5.6a4.8 4.8 0 0 1-2.08 3.15v2.6h3.36C20.84 18 22 15.4 22 12.2Z" /><path fill="#34A853" d="M12 22c2.7 0 4.96-.9 6.62-2.4l-3.36-2.6c-.93.62-2.12.98-3.26.98-2.5 0-4.62-1.69-5.38-3.96H3.15v2.69A10 10 0 0 0 12 22Z" /><path fill="#FBBC05" d="M6.62 13.02a6 6 0 0 1 0-3.84V6.49H3.15a10 10 0 0 0 0 9.02l3.47-2.49Z" /><path fill="#EA4335" d="M12 6.58c1.47 0 2.78.5 3.82 1.5l2.85-2.85A9.9 9.9 0 0 0 12 2 10 10 0 0 0 3.15 6.49l3.47 2.69C7.38 8.27 9.5 6.58 12 6.58Z" /></svg>
  )
  return ( // steam
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden><path d="M12 2a10 10 0 0 0-9.96 9.18l5.37 2.22a2.83 2.83 0 0 1 1.6-.5h.14l2.39-3.46v-.05a3.78 3.78 0 1 1 3.78 3.78h-.09l-3.4 2.43v.12a2.84 2.84 0 0 1-5.6.65L2.4 14.3A10 10 0 1 0 12 2Zm-3.5 15.18-1.23-.5a2.13 2.13 0 0 0 3.92-1.66 2.13 2.13 0 0 0-2.8-1.13l1.27.53a1.57 1.57 0 1 1-1.2 2.9Zm9-7.4a2.52 2.52 0 1 0-2.52-2.52A2.52 2.52 0 0 0 17.5 9.78Zm0-4.4a1.89 1.89 0 1 1-1.89 1.88 1.89 1.89 0 0 1 1.89-1.88Z" /></svg>
  )
}

export function AuthModal({
  auth, onSignedIn, initialMode = 'signin', resetToken, onClose,
  origin = '', external = false, refCode = null, providerHref, onProvider,
}: {
  auth: AuthClient
  onSignedIn: () => Promise<unknown> | void
  initialMode?: AuthMode; resetToken?: string; onClose: () => void
  // Where /terms and /privacy live ('' on the site) and whether they open in a new tab.
  origin?: string; external?: boolean
  // Invite code to attribute a signup to (the site reads dzm_ref).
  refCode?: string | null
  // OAuth, one of two ways: the site links to its redirect URL (a full
  // navigation); the launcher completes the round-trip in its own window and
  // resolves true once signed in, false when the user gave up. With neither,
  // the provider buttons stay hidden.
  providerHref?: (p: AuthProvider) => string
  onProvider?: (p: AuthProvider) => Promise<boolean>
}) {
  const ext = external ? { target: '_blank', rel: 'noopener noreferrer' } : { target: '_blank', rel: 'noopener' }
  const [mode, setMode] = useState<AuthMode>(resetToken ? 'reset' : initialMode)
  const [providers, setProviders] = useState<Record<AuthProvider, boolean> | null>(null)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [busy, setBusy] = useState(false)
  const [wantsUpdates, setWantsUpdates] = useState(false)
  const [agreedTos, setAgreedTos] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [success, setSuccess] = useState<{ heading: string; body: string; cta: string } | null>(null)

  useEffect(() => { auth.providers().then(setProviders).catch(() => setProviders(null)) }, [auth])

  // Steam is intentionally omitted: its OpenID 2.0 gives no email, which the
  // account-linking + notifications flow needs. Re-add 'steam' here to restore it.
  const enabledProviders = providerHref || onProvider ? (['discord', 'google'] as AuthProvider[]).filter((p) => providers?.[p]) : []
  const providerClass = `w-full flex items-center justify-center gap-2.5 rounded-lg h-11 px-4
                        bg-[var(--background-tertiary)] text-[var(--foreground)] hover:bg-[var(--surface-1-hover)]
                        transition-colors text-sm font-medium`

  async function withProvider(p: AuthProvider) {
    if (!onProvider) return
    setErr(null); setBusy(true)
    try {
      if (await onProvider(p)) { await onSignedIn(); onClose() }
    } catch (e2) {
      setErr((e2 as Error).message || 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    setErr(null); setBusy(true)
    try {
      if (mode === 'signin') {
        await auth.login(email.trim(), password)
        await onSignedIn(); onClose()
      } else if (mode === 'signup') {
        await auth.register(email.trim(), password, refCode, wantsUpdates)
        setSuccess({
          heading: 'Verify your email',
          body: `We’ve sent a confirmation link to ${email.trim()}. Click it to activate your account — then you can sign in.`,
          cta: 'Got it',
        })
      } else if (mode === 'forgot') {
        await auth.requestReset(email.trim())
        setSuccess({
          heading: 'Check your email',
          body: 'If that email has an account, a reset link is on its way.',
          cta: 'Done',
        })
      } else if (mode === 'reset') {
        if (!resetToken) throw new Error('Missing reset token.')
        await auth.reset(resetToken, password)
        await onSignedIn()
        setSuccess({
          heading: 'Password updated',
          body: 'Your password is set and you’re signed in. Any other sessions have been signed out.',
          cta: 'Continue',
        })
      }
    } catch (e2) {
      setErr((e2 as Error).message || 'Something went wrong.')
    } finally {
      setBusy(false)
    }
  }

  const title = mode === 'signup' ? 'Create your account'
    : mode === 'forgot' ? 'Reset your password'
    : mode === 'reset' ? 'Choose a new password' : 'Sign in'

  return (
    <Modal.Backdrop isOpen onOpenChange={(o) => !o && onClose()}>
      <Modal.Container>
        <Modal.Dialog className="max-w-[420px] w-full">
          <Modal.Header><Modal.Heading className="text-lg">{success ? success.heading : title}</Modal.Heading></Modal.Header>
          <Modal.Body className="space-y-4">
            {success ? (
            <div className="text-center py-2">
              <div className="mx-auto mb-4 w-12 h-12 rounded-full inline-flex items-center justify-center text-2xl bg-[var(--brand-soft)] text-[var(--brand)]">✓</div>
              <p className="text-[14px] text-[var(--muted)] leading-relaxed">{success.body}</p>
              <Button className="w-full mt-6" onPress={onClose}>{success.cta}</Button>
            </div>
            ) : (
            <>
            {/* OAuth providers (only when configured + on the credential modes) */}
            {(mode === 'signin' || mode === 'signup') && enabledProviders.length > 0 && (
              <>
                <div className="space-y-2">
                  {enabledProviders.map((p) => providerHref ? (
                    <a key={p} href={providerHref(p)} className={providerClass}>
                      <ProviderGlyph p={p} /> Continue with {PROVIDER_LABEL[p]}
                    </a>
                  ) : (
                    <button key={p} type="button" disabled={busy} onClick={() => void withProvider(p)} className={providerClass}>
                      <ProviderGlyph p={p} /> Continue with {PROVIDER_LABEL[p]}
                    </button>
                  ))}
                </div>
                {mode === 'signup' && (
                  <p className="text-[11px] text-[var(--muted)] leading-relaxed text-center">
                    By continuing you agree to our{' '}
                    <a href={`${origin}/terms`} {...ext} className="underline underline-offset-2 hover:text-[var(--foreground)]">Terms</a>{' '}and{' '}
                    <a href={`${origin}/privacy`} {...ext} className="underline underline-offset-2 hover:text-[var(--foreground)]">Privacy Policy</a>.
                  </p>
                )}
                <div className="flex items-center gap-3 text-[var(--muted)]">
                  <span className="h-px flex-1 bg-[var(--border)]" />
                  <span className="mono text-[10px] uppercase tracking-wider">or email</span>
                  <span className="h-px flex-1 bg-[var(--border)]" />
                </div>
              </>
            )}

            <form onSubmit={submit} className="space-y-3">
              {mode !== 'reset' && (
                <div>
                  <label className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">Email</label>
                  <input type="email" required autoComplete="email" value={email}
                    onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                    className="field-input px-3 w-full mt-1" />
                </div>
              )}
              {mode !== 'forgot' && (
                <div>
                  <label className="mono text-[10px] uppercase tracking-wider text-[var(--muted)]">
                    {mode === 'reset' ? 'New password' : 'Password'}
                  </label>
                  <input type="password" required minLength={8}
                    autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                    value={password} onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signin' ? '••••••••' : 'At least 8 characters'}
                    className="field-input px-3 w-full mt-1" />
                </div>
              )}

              {mode === 'signup' && (
                <label className="flex items-start gap-2.5 text-[13px] text-[var(--muted)] cursor-pointer select-none pt-0.5">
                  <input type="checkbox" checked={agreedTos} onChange={(e) => setAgreedTos(e.target.checked)}
                    className="mt-0.5 accent-[var(--brand)] w-4 h-4 shrink-0" />
                  <span>I agree to the{' '}
                    <a href={`${origin}/terms`} {...ext} className="text-[var(--foreground)] underline underline-offset-2">Terms of Service</a>{' '}and{' '}
                    <a href={`${origin}/privacy`} {...ext} className="text-[var(--foreground)] underline underline-offset-2">Privacy Policy</a>.
                  </span>
                </label>
              )}
              {mode === 'signup' && (
                <label className="flex items-start gap-2.5 text-[13px] text-[var(--muted)] cursor-pointer select-none">
                  <input type="checkbox" checked={wantsUpdates} onChange={(e) => setWantsUpdates(e.target.checked)}
                    className="mt-0.5 accent-[var(--brand)] w-4 h-4 shrink-0" />
                  <span>Email me about new &amp; notable servers, stats and DayZ news. Optional — unsubscribe any time.</span>
                </label>
              )}

              {err && <p className="text-[13px] text-red-400">{err}</p>}

              <Button type="submit" isDisabled={busy || (mode === 'signup' && !agreedTos)} className="w-full">
                {busy ? 'Working…'
                  : mode === 'signup' ? 'Create account'
                  : mode === 'forgot' ? 'Send reset link'
                  : mode === 'reset' ? 'Set new password' : 'Sign in'}
              </Button>
            </form>

            {/* Mode switches */}
            <div className="text-[13px] text-[var(--muted)] space-y-1.5 pt-1">
              {mode === 'signin' && <>
                <p>New here? <button type="button" className="text-[var(--foreground)] underline underline-offset-2" onClick={() => { setErr(null); setMode('signup') }}>Create an account</button></p>
                <p><button type="button" className="text-[var(--foreground)] underline underline-offset-2" onClick={() => { setErr(null); setMode('forgot') }}>Forgot your password?</button></p>
              </>}
              {mode === 'signup' && <p>Already have an account? <button type="button" className="text-[var(--foreground)] underline underline-offset-2" onClick={() => { setErr(null); setMode('signin') }}>Sign in</button></p>}
              {(mode === 'forgot' || mode === 'reset') && <p><button type="button" className="text-[var(--foreground)] underline underline-offset-2" onClick={() => { setErr(null); setMode('signin') }}>Back to sign in</button></p>}
            </div>
            </>
            )}
          </Modal.Body>
        </Modal.Dialog>
      </Modal.Container>
    </Modal.Backdrop>
  )
}
